/**
 * PortalTrocarSenha
 *
 * Spec: spec-force-password-change.md
 * Troca OBRIGATÓRIA de senha do PORTAL (1º acesso com senha temporária).
 * Rota top-level (sem o PortalLayout). O ProtectedPortalRoute redireciona pra cá
 * enquanto `password_change_required` estiver true.
 *
 * Fluxo: senha atual (temporária) + nova → POST /portal/auth/change-password →
 * limpa o flag no contexto e volta pro Início. Logout sempre disponível.
 */
import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, KeyRound, Loader2, LogOut } from "lucide-react";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { portalAuthApi, getErrorMessage } from "@/services/portalApi";

const PortalTrocarSenha: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout, clearPasswordChangeRequired } = usePortalAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Durante a rehidratação do auth, evita flash-redirect pro login.
  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-slate-500">Carregando...</div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }
  if (!user?.password_change_required) {
    return <Navigate to="/portal/inicio" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("A nova senha deve ter no mínimo 8 caracteres.");
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError("A senha deve conter ao menos uma maiúscula, uma minúscula e um número.");
      return;
    }
    if (newPassword !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setSubmitting(true);
    try {
      await portalAuthApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      clearPasswordChangeRequired();
      navigate("/portal/inicio", { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-emerald-50/40 px-4">
      <div className="w-full max-w-md rounded-xl border border-emerald-200/40 bg-white/90 p-6 shadow-[0_12px_40px_rgba(16,78,50,0.08)] sm:p-8">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <KeyRound className="h-6 w-6" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight text-emerald-800">
            Trocar senha
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Por segurança, defina uma nova senha antes de continuar — você está usando uma senha
            temporária.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50/80">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="portal-current-password">Senha atual (temporária)</Label>
            <Input
              id="portal-current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              disabled={submitting}
              className="border-emerald-200/60 focus-visible:ring-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="portal-new-password">Nova senha</Label>
            <Input
              id="portal-new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
              disabled={submitting}
              className="border-emerald-200/60 focus-visible:ring-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="portal-confirm-password">Confirme a nova senha</Label>
            <Input
              id="portal-confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
              className="border-emerald-200/60 focus-visible:ring-emerald-500"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              "Trocar senha e continuar"
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={logout}
            disabled={submitting}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sair
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PortalTrocarSenha;
